import { useCallback, useState } from "react";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ActionLink } from "@hopper-b2b/ui";
import clsx from "clsx";
import styles from "./styles.module.scss";

interface AdditionalContext {
  passengerId?: string;
}

interface CountPickerRowProps {
  title: string;
  subtitle?: string;
  minCount?: number;
  maxCount?: number;
  baseCount: number;
  updateCount: (count: number, additionalContext?: AdditionalContext) => void;
  additionalContext?: AdditionalContext;
}

export const CountPickerRow = ({
  title,
  subtitle,
  minCount,
  maxCount = 0,
  baseCount,
  updateCount,
  additionalContext,
}: CountPickerRowProps) => {
  const [count, setCount] = useState(baseCount ?? 0);
  const minusDisabled = count <= (minCount ?? 0);

  const onChangeHandler = useCallback(
    (newCount: number) => {
      setCount(newCount);
      updateCount(newCount, additionalContext);
    },
    [additionalContext, updateCount]
  );

  return (
    <div className={styles.CountPickerRow}>
      <div className="left">
        <h4 className="title">{title}</h4>
        {subtitle ? <p className="subtitle">{subtitle}</p> : null}
      </div>
      <div className="count-picker">
        <ActionLink
          className={"count-picker-button"}
          disabled={minusDisabled}
          onClick={() => {
            if (!minusDisabled) {
              onChangeHandler(count - 1);
            }
          }}
          content={
            <div
              className={clsx("button-content-wrapper", {
                disabled: minusDisabled,
              })}
            >
              <FontAwesomeIcon
                icon={faMinus as IconProp}
                className={clsx("icon", "minus", minusDisabled && "disabled")}
              />
            </div>
          }
        />
        <div className="count-number-container">
          <div className="count-number">
            {count}
            <span className="count-alert" aria-live="polite" aria-atomic={true}>
              {subtitle ? `${count} ${title} ${subtitle}` : `${count} ${title}`}
            </span>
          </div>
        </div>
        <ActionLink
          className={"count-picker-button"}
          onClick={() => onChangeHandler(count + 1)}
          disabled={count === maxCount}
          content={
            <div
              className={clsx("button-content-wrapper", {
                disabled: count === maxCount,
              })}
            >
              <FontAwesomeIcon
                icon={faPlus as IconProp}
                className={clsx("icon", "plus")}
              />
            </div>
          }
        />
      </div>
    </div>
  );
};
